import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Switch, TextField, Typography } from "@mui/material";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { useState } from "react";
import { IFacturaErrors, validateFactura, getCurrencySymbol } from "../../../utils/Factura";
import * as constantes from "../../../common/Constantes";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ConfigUtils from "../../../utils/Config";
import { IFacturaMetodoPago } from "../facturarCita/FacturarCitaDialog";
import IAlert from "../../../types/Alert";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import { useNavigate } from "react-router-dom";
import { IConcepto, IImpuesto } from "../facturarCita/DetalleFactura";
import FacturacionService from "../../../services/FacturacionService";

interface RectificarFacturaBonoProps {
  factura: IFacturaCompleta;
  pacienteId: string;
  open: boolean;
  onClose: () => void;
  onFacturaRectificada?: (facturaRectificativa: IFacturaCompleta) => void;
}

export interface IRectificativaPayload {
  userId: string;
  pacienteId: string;
  servicio: string;
  conceptos: IConcepto[];
  impuestos: IImpuesto[];
  tipoFactura: string;
  metodoPago?: IFacturaMetodoPago | undefined;
  facturaOriginalId: string;
}

const RectificarFacturaBono = ({ factura, pacienteId, open, onClose, onFacturaRectificada }: RectificarFacturaBonoProps) => {
    const simboloMoneda = getCurrencySymbol(factura.moneda);
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<IFacturaErrors>({});
    const [modalConfirm, setModalConfirm] = useState(false);
    const [nuevaFacturaRectificativa, setNuevaFacturaRectificativa] = useState<IRectificativaPayload | null>(null);

    const [servicio, setServicio] = useState(factura.servicio);
    const [concepto, setConcepto] = useState(factura.conceptos[0].nombre);
    const [inputPrecioBase, setInputPrecioBase] = useState(factura.conceptos[0].precio.toFixed(2).replace(".", ","));
    const [precio, setPrecio] = useState(factura.conceptos[0].precio);
    const [impuesto, setImpuesto] = useState(factura.impuestos[0].cuota);
    const [precioTotal, setPrecioTotal] = useState(factura.conceptos[0].precio + (factura.conceptos[0].precio * factura.impuestos[0].cuota) / 100);
    const [facturaPagada, setFacturaPagada] = useState(!!factura.metodoPago);
    const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(() => {
      if (!factura.metodoPago) return undefined;

      return {
        ...factura.metodoPago,
        fechaPago: moment(factura.metodoPago.fechaPago),
      };
    });

    const metodosPago = ConfigUtils.getMetodosPago();
    const userRole: number = ConfigUtils.getUserRole();

    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

    const validateRectificativa = () => {
      setErrors(prev => ({ ...prev, general: undefined }));
      const validationErrors = validateFactura({
        tipoPago: "total",
        selectedServicio: undefined,
        servicio,
        concepto,
        precio,
        facturable: true,
        facturaPagada,
        metodoPago,
        isEdit: true
      }); 
    
      setErrors(validationErrors);  
    
      if (Object.keys(validationErrors).length > 0) return;
    
      const original = factura;
      const mismaFechaPago =
      original.metodoPago?.fechaPago &&
      metodoPago?.fechaPago &&
        moment(original.metodoPago.fechaPago).isSame(
        moment(metodoPago.fechaPago),
        'day'
      );
      const cambiosFiscales =
        original.servicio !== servicio ||
        original.conceptos[0].nombre !== concepto ||
        original.conceptos[0].precio !== precio ||
        original.impuestos[0].cuota !== impuesto;

      const cambioPago =
        original.metodoPago?.nombre !== metodoPago?.nombre ||
        original.metodoPago?.codigo !== metodoPago?.codigo ||
        !mismaFechaPago;

      if (!cambiosFiscales && !cambioPago) {
        setErrors(prev => ({ ...prev, general: "No se han realizado cambios respecto a la factura original" }));
        return;
      }

      if (!cambiosFiscales && cambioPago) {
        setErrors(prev => ({ ...prev, general: "Solo has cambiado el método o la fecha de pago — usa \"Corregir cobro\" en vez de crear una rectificativa" }));
        return;
      }

      const payload: IRectificativaPayload = {
        userId: ConfigUtils.getUserId(),
        pacienteId,
        servicio,
        conceptos: [{
          nombre: concepto,
          cantidad: 1,
          precio: precio
        }],
        impuestos: [{
          tipo: "IVA",
          base: precio,
          cuota: impuesto
        }],
        tipoFactura: original.tipo,
        metodoPago: metodoPago,
        facturaOriginalId: original.numero ?? ""
      };    
      setNuevaFacturaRectificativa(payload);
      setModalConfirm(true);
    };

    const handleConfirmRectificativa = async () => {
        if (!nuevaFacturaRectificativa) return;
        setModalConfirm(false);
        setIsLoading(true);
        try {
          const response = await FacturacionService.createFacturaRectificativaCore(nuevaFacturaRectificativa);
          onClose();
          if (onFacturaRectificada) onFacturaRectificada(response.data.factura);
          setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "RectificarFacturaBono.Component.handleConfirmRectificativa",
              level: "error",
              message: e.message, 
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} maxWidth="sm" fullWidth onClose={isLoading ? undefined : onClose}>
                <DialogTitle>{`Rectificar factura nº: ${factura.numero}`}</DialogTitle>
                <DialogContent dividers>
                    
                  <Grid container spacing={{ xs: 0, md: 2 }}>
                    {/* Servicio */}
                    <Grid item xs={12} md={6}>
                    <TextField
                      label="Servicio*"
                      fullWidth
                      sx={{ mb: 2 }}
                      value={servicio}
                      onChange={(e) => {
                        setServicio(e.target.value);
                        setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                        setErrors((prev: IFacturaErrors) => ({ ...prev, servicioName: undefined }));
                      }}
                      error={!!errors.servicioName}
                      helperText={errors.servicioName}
                    />
                    </Grid>
                    {/* Concepto */}
                    <Grid item xs={12} md={6}>
                    <TextField
                      label="Concepto*"
                      fullWidth
                      sx={{ mb: 2 }}
                      value={concepto}
                      onChange={(e) => {
                        setConcepto(e.target.value);
                        setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                        setErrors((prev: IFacturaErrors) => ({ ...prev, concepto: undefined }));
                      }}
                      error={!!errors.concepto}
                      helperText={errors.concepto}
                    />
                    </Grid>
                  </Grid>  

                  <Grid container spacing={{ xs: 0, md: 2 }}>
                    {/* Precio */}
                    <Grid item xs={12} md={4}>
                    <TextField
                      label={`Precio base (${simboloMoneda})*`}
                      fullWidth
                      type="text"
                      sx={{ mb: 2 }}
                      value={inputPrecioBase}
                      onChange={(e) => {
                        let raw = e.target.value;
                        setInputPrecioBase(raw);

                        const normalized = raw.replace(",", ".");
                        if (/^\d*([.,]\d*)?$/.test(raw)) {
                          setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                          const nuevoPrecio = parseFloat(normalized);
                        
                          if (!isNaN(nuevoPrecio)) {
                            setPrecio(nuevoPrecio);
                            setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
                          } else {
                            setPrecio(0);
                            setPrecioTotal(0);
                          }
                        }
                      }}
                      onBlur={() => {
                        const formatted = precio.toFixed(2).replace(".", ",");
                        setInputPrecioBase(formatted);
                      }}
                      error={!!errors.precio}
                      helperText={errors.precio}
                    />
                    </Grid>
                    {/* Impuesto */}
                    <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
                      <Select
                        labelId="select-impuesto-label"
                        value={impuesto}
                        label="Impuesto (%)*"
                        onChange={(e) => {
                          const nuevoImp = Number(e.target.value);
                          setImpuesto(nuevoImp);
                          setPrecioTotal(precio + (precio * nuevoImp) / 100);
                          setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                        }}
                      >
                        {constantes.IMPUESTOS.map((imp) => (
                          <MenuItem key={imp} value={imp}>
                            {imp}%
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={`Precio total (${simboloMoneda})`}
                      value={precioTotal.toFixed(2).replace(".", ",")}
                      InputProps={{ readOnly: true, sx: {
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#bdbdbd'
                        }
                      }}}
                    />
                    </Grid>
                  </Grid>

                    {/* Bloque de Pago */}
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      {/* Texto explicativo */}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ flexBasis: "100%", flexGrow: 1 }}
                      >
                        * Si la factura se marca como pagada, el método de pago es obligatorio.
                      </Typography>

                      {/* Switch de factura pagada */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={facturaPagada}
                            onChange={(e) => {
                              const pagada = e.target.checked;

                              setFacturaPagada(pagada);
                              if (!pagada) setMetodoPago(undefined);
                            
                              setErrors((prev: IFacturaErrors) => ({
                                ...prev,
                                metodoPago: undefined,
                                fechaPago: undefined
                              }));
                            }}
                          />
                        }
                        label="Factura pagada"
                        sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
                      />

                      {/* Select de método de pago */}
                      {facturaPagada && (
                        <Grid container spacing={2} alignItems="flex-start">
                          {/* Método de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <FormControl fullWidth error={!!errors.metodoPago}>
                              <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                              <Select
                                labelId="metodo-pago-label"
                                value={metodoPago?.nombre || ""}
                                label="Método de pago*"
                                onChange={(e) => {
                                  const nombre = e.target.value;
                                  const metodo = metodosPago.find((m) => m.nombre === nombre);

                                  if (!metodo) return;

                                  setMetodoPago({
                                    nombre: metodo.nombre,
                                    codigo: metodo.codigo,
                                    fechaPago: metodoPago?.fechaPago || moment(),
                                  });

                                  setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                                  setErrors((prev: IFacturaErrors) => ({ ...prev, metodoPago: undefined }));
                                }}
                              >
                                {metodosPago.filter((m) => m.activo).map((m) => (
                                  <MenuItem key={m.codigo} value={m.nombre}>
                                    {m.nombre}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.metodoPago && (
                                <Typography color="error" variant="caption">
                                  {errors.metodoPago}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                          
                          {/* Fecha de pago */}
                          <Grid item xs={12} sm={6} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                label="Fecha de pago"
                                value={metodoPago?.fechaPago || null}
                                onChange={(date) => {
                                  if (metodoPago) {
                                    setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                                    setErrors((prev: IFacturaErrors) => ({ ...prev, general: undefined }));
                                    setErrors((prev: IFacturaErrors) => ({ ...prev, fechaPago: undefined }));
                                  }
                                }}
                                renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} /> )}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      )}
                      {errors.general && <Alert sx={{ mt: 1 }} severity="warning">{errors.general}</Alert>}
                    </Box>

                </DialogContent>
                <DialogActions>
                  <Button onClick={onClose} disabled={isLoading}>
                    Cerrar
                  </Button>

                  {/* Botón "Crear rectificativa" */}
                  {userRole === constantes.ROLES.ADMINISTRADOR &&
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={validateRectificativa}
                    size="small"
                  >
                    Crear rectificativa
                  </Button>
                  }
                </DialogActions>
            </Dialog>

            {modalConfirm && (
              <Dialog open={modalConfirm} onClose={() => {setModalConfirm(false)}}>
                <DialogTitle>Confirmar creación de rectificativa</DialogTitle>
                <DialogContent>
                  <Typography>
                    Se generará una factura rectificativa vinculada a la factura original nº: <b>{factura.numero}</b>.
                    ¿Deseas continuar?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button 
                    onClick={() => {
                      setModalConfirm(false);
                      setNuevaFacturaRectificativa(null);
                    }}
                  >Cancelar</Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmRectificativa}
                  >
                    Confirmar
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
              <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
                {alert.msg}
              </Alert>
            </Snackbar>
        </>
    );
};

export default RectificarFacturaBono;